import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";

/**
 * Server-side resolution of the exclusion cascade (#6848).
 *
 * ## The defect this closes
 *
 * Excluding a claimant cascades through the records the client has LOADED.
 * On a paged review a linked claim_item or reimbursement row on an unfetched
 * page is not excluded — and on approve those lines load against a claimant
 * the reviewer believed they had removed, contributing to paid amounts and
 * YTD. Nothing reports a problem: the screen said the exclusion worked.
 *
 * Paging engages at REVIEW_AUTOLOAD_MAX_RECORDS (5,000), so this bites well
 * below the 497k document that motivated the work.
 *
 * ## Why this resolves at APPROVE time
 *
 * The interactive cascade (getLinkedRecords over loaded records) stays as it
 * is: instant, and a strict SUBSET of the right answer, so it never removes
 * something that should stay excluded. What it cannot be is complete.
 *
 * Approve is where completeness becomes correctness — it is the moment the
 * indices become money. Resolving here means the payload is right even if the
 * reviewer never scrolled far enough for the interactive cascade to see the
 * linked rows.
 */

export type CascadeOutcome =
  /** Resolved (or provably unnecessary). `indices` is the authoritative set. */
  | { status: "resolved"; indices: number[] }
  /**
   * Could not be resolved, and the document is paged, so the exclusion set
   * may be incomplete. Approval must NOT proceed — see needsServerCascade.
   */
  | { status: "blocked"; reason: "unavailable" | "failed"; message: string };

/**
 * Where a caller's exclusion indices come from. This is what decides whether
 * the cascade needs server-side resolution — not how many records happen to
 * be loaded.
 *
 * A discriminated union rather than three optional numbers, and that is the
 * result of getting it wrong: the numbers began optional with defaults of
 * 0/0/false, which reads as "total unknown, so ask the server". Correct for a
 * caller mid-load, wrong for a caller that has no record set at all —
 * MultiClientReviewPanel derives its indices from the projection and never
 * calls the records endpoint, so it silently opted into a resolution it does
 * not need and every BOB approval with an exclusion was blocked.
 *
 * The union makes the question unavoidable and answerable: a caller states
 * what its index source IS, rather than supplying numbers about a record set
 * it may not have.
 */
export type ExclusionIndexScope =
  | {
      /**
       * Indices come from the paged record read (`useTransformationRecords`),
       * so they cover the loaded pages only and a claimant's other rows may
       * be unfetched. Needs server resolution unless the whole set is loaded.
       */
      source: "loaded-records";
      loadedRecordCount: number;
      totalRecordCount: number;
      /**
       * True once `totalRecordCount` is authoritative. Load-bearing: before
       * the first page lands `total` is 0 and `loadedCount >= total` reads as
       * "complete", which is the useState(0) trap that fired the unwindowed
       * 2.34 GiB projection in the #6755 review.
       */
      totalRecordCountKnown: boolean;
    }
  | {
      /**
       * Indices come from a server-computed whole-document projection — every
       * `extracted_record_index` the surface can offer is already in hand, and
       * the cascade over them is complete by construction.
       *
       * This is the BOB panel: `claimantRowIndices` maps
       * `claimant.rows[].extracted_record_index` straight off the projection,
       * so excluding a client already covers every one of its claimants' rows
       * across the whole document. There is no record window to be blind to.
       */
      source: "whole-document-projection";
    };

/**
 * Whether the server has to be asked at all.
 *
 * False in the cases where the caller already holds the complete answer:
 *
 *  - Nothing is excluded. There is no cascade to expand.
 *  - The indices come from a whole-document projection. Complete by source.
 *  - Every record is loaded. `getLinkedRecords` searched the whole set, so its
 *    answer IS the whole-set answer — every document below the auto-load
 *    bound, i.e. almost all of them. That is #6755 AC5's "no additional round
 *    trips for documents that fit in one window".
 */
export const needsServerCascade = ({
  excludedCount,
  scope,
}: {
  excludedCount: number;
  scope: ExclusionIndexScope;
}): boolean => {
  if (excludedCount === 0) return false;
  if (scope.source === "whole-document-projection") return false;
  if (!scope.totalRecordCountKnown) return true;
  return scope.loadedRecordCount < scope.totalRecordCount;
};

interface LinkageResponse {
  record_indices?: number[];
}

/**
 * Expands excluded indices to every record sharing a claimant with them.
 *
 * The server returns the seeds as well as their siblings, so the result is
 * the exclusion set rather than something to merge with the input.
 */
export const fetchLinkedRecordIndices = async (
  documentId: string,
  transformationId: string,
  recordIndices: number[]
): Promise<number[]> => {
  const url =
    `${getPartnerAwareApiUrl()}/documents/${documentId}` +
    `/document-transformations/${transformationId}/records/claimant-linkage`;

  const { json } = await httpClient(url, {
    method: "POST",
    body: JSON.stringify({ record_indices: recordIndices }),
  });

  return (json as LinkageResponse)?.record_indices ?? [];
};

/**
 * True for the 409 the endpoints answer when a transformation's record index
 * predates migration 000220 or was written under a superseded identity.
 *
 * Distinguished from every other failure because the two need OPPOSITE
 * handling, and getting that backwards is how this closes a hole by opening a
 * different one:
 *
 *  - 409 is a permanent property of the document. Retrying cannot help; the
 *    remedy is a reprocess — reachable since #6920 from the review screen's
 *    Reprocess button and the queue's row action, so the advice is actionable
 *    rather than merely correct.
 *  - Anything else (network, 500, timeout) is transient and worth retrying.
 *
 * Both block approval. Only the message differs.
 */
export const isRecordIndexUnavailable = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  return (error as { status?: number }).status === 409;
};

/**
 * Resolves the authoritative exclusion set for an approval.
 *
 * BLOCKS rather than degrading when it cannot answer, which is the whole
 * point. Falling back to the loaded-only set here would reproduce the exact
 * defect #6848 exists to remove, except now with a code path that looked like
 * it had handled it.
 *
 * The 409 case is a real loss of function for documents processed before
 * 000220 — they could be approved before this change. That is deliberate: on
 * a PAGED document with exclusions, "approved" previously meant "approved
 * with an exclusion set that is probably incomplete". Refusing, with a
 * message naming the remedy, is the honest version of that.
 */
export const resolveExclusionCascade = async ({
  documentId,
  transformationId,
  excludedIndices,
  scope,
}: {
  documentId: string | undefined;
  transformationId: string | undefined;
  excludedIndices: number[];
  scope: ExclusionIndexScope;
}): Promise<CascadeOutcome> => {
  if (!needsServerCascade({ excludedCount: excludedIndices.length, scope })) {
    return { status: "resolved", indices: excludedIndices };
  }

  if (!documentId || !transformationId) {
    return {
      status: "blocked",
      reason: "failed",
      message: "Cannot verify which records are linked to your exclusions. Reload the page and try again.",
    };
  }

  try {
    const indices = await fetchLinkedRecordIndices(documentId, transformationId, excludedIndices);
    return { status: "resolved", indices };
  } catch (error) {
    if (isRecordIndexUnavailable(error)) {
      return {
        status: "blocked",
        reason: "unavailable",
        message:
          "This document was processed before linked-record exclusion was available, " +
          "so records on unloaded pages cannot be checked. Reprocess the document to approve with exclusions.",
      };
    }
    return {
      status: "blocked",
      reason: "failed",
      message: "Could not confirm which records are linked to your exclusions. Try approving again.",
    };
  }
};
